import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase, faTrashAlt, faExclamationTriangle,
  faCheckCircle, faTimes, faLock, faSyncAlt,
  faTable, faSearch, faFilter, faServer,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_LIST = `${API_CONFIG2.apiUrl}admin/list_tables.php`;
const API_RESET = `${API_CONFIG2.apiUrl}admin/reset_table.php`;

// --- Fond de particules réseau (identique aux autres dashboards) ---
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animFrame = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width, height;
    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };
    const initParticles = () => {
      particles.current = Array.from({ length: 60 }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 1,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const pts = particles.current;
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.25)';
        ctx.fill();
        for (let j = i + 1; j < pts.length; j++) {
          const p2 = pts[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animFrame.current = requestAnimationFrame(draw);
    };
    resize();
    window.addEventListener('resize', resize);
    animFrame.current = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener('resize', resize);
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.6, 
      }}
    />
  );
};

const ResetAllData = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [password, setPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  const cardIndex = useRef(0);

  const fetchTables = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_LIST);
      if (!res.ok) throw new Error('Impossible de récupérer la liste des tables');
      const data = await res.json();
      if (data.status === 'success') {
        setTables(data.data);
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const showToast = (message, variant = 'success') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 5000);
  };

  const openResetModal = (table) => {
    setSelectedTable(table);
    setPassword('');
    setShowModal(true);
  };

  const handleReset = async () => {
    if (!password) {
      showToast('Mot de passe requis', 'warning');
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(API_RESET, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table: selectedTable.name,
          password: password,
          confirm: true
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        showToast(`Table "${selectedTable.name}" vidée`, 'success');
        setTables(prev =>
          prev.map(t => (t.name === selectedTable.name ? { ...t, rows: 0 } : t))
        );
        setShowModal(false);
      } else {
        showToast(data.message || 'Échec de la réinitialisation', 'danger');
      }
    } catch (err) {
      showToast('Erreur réseau', 'danger');
    } finally {
      setResetting(false);
    }
  };

  const totalRows = tables.reduce((acc, t) => acc + t.rows, 0);

  // Gestion des délais d'animation pour les cartes
  cardIndex.current = 0;
  const getNextDelay = () => {
    cardIndex.current += 1;
    return cardIndex.current * 60;
  };

  // --- Rendu du contenu ---
  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-secondary mt-2">Chargement des tables...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-5 text-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="mb-3" />
          <p>Erreur : {error}</p>
          <button className="monetra-btn primary" onClick={fetchTables}>
            <FontAwesomeIcon icon={faSyncAlt} className="me-2" /> Réessayer
          </button>
        </div>
      );
    }

    return (
      <>
        {/* En-tête avec résumé */}
        <div className="fade-up" style={{ animationDelay: '0ms' }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold text-white mb-1">
                <FontAwesomeIcon icon={faDatabase} className="text-primary me-2" />
                Tables de la base de données
              </h2>
              <p className="text-secondary m-0">
                Gérez chaque table individuellement – {totalRows} enregistrement(s) au total
              </p>
            </div>
            <button className="monetra-btn outline" onClick={fetchTables} disabled={loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading} /> Actualiser
            </button>
          </div>
        </div>

        {/* Grille des tables */}
        <div className="table-grid">
          {tables.map((table, index) => (
            <div
              key={table.name}
              className="table-card fade-up"
              style={{ animationDelay: `${getNextDelay()}ms` }}
            >
              <div className="table-icon">
                <FontAwesomeIcon icon={faTable} />
              </div>
              <div className="table-details">
                <div className="table-name">{table.name}</div>
                <div className="table-rows">
                  {table.rows} ligne(s)
                </div>
                <div className="progress-track">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${table.rows > 0 ? Math.min(100, (table.rows / (totalRows || 1)) * 100) : 0}%`
                    }}
                  ></div>
                </div>
              </div>
              <button
                className="btn-reset-table"
                onClick={() => openResetModal(table)}
                disabled={table.rows === 0}
                title={table.rows === 0 ? 'Table vide' : 'Vider cette table'}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span>Vider</span>
              </button>
            </div>
          ))}
        </div>

        {tables.length === 0 && (
          <div className="text-center py-5 text-secondary">
            <FontAwesomeIcon icon={faInfoCircle} size="2x" className="mb-2 opacity-50" />
            <p>Aucune table trouvée.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 18px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .reset-container {
          position: relative;
          z-index: 1;
          background: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          min-height: 100vh;
          padding: 2rem;
          color: var(--text-primary);
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* Animation d'apparition */
        .fade-up {
          opacity: 0;
          animation: fadeInUp 0.5s ease forwards;
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Boutons Monetra */
        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.04);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        /* Grille des tables */
        .table-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.2rem;
          margin-top: 1rem;
        }

        .table-card {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.2rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 1.2rem;
          transition: all 0.25s ease;
          box-shadow: var(--shadow);
        }
        .table-card:hover {
          background: rgba(20, 40, 80, 0.65);
          border-color: rgba(59, 130, 246, 0.25);
          transform: translateY(-4px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.6);
        }

        .table-icon {
          width: 48px;
          height: 48px;
          background: rgba(59, 130, 246, 0.12);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent-blue);
          font-size: 1.3rem;
          flex-shrink: 0;
        }

        .table-details {
          flex: 1;
          min-width: 0;
        }
        .table-name {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .table-rows {
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin: 4px 0;
        }
        .progress-track {
          height: 4px;
          background: rgba(255,255,255,0.05);
          border-radius: 2px;
          margin-top: 8px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--accent-blue), var(--accent-dark-blue));
          border-radius: 2px;
          transition: width 0.4s ease;
        }

        .btn-reset-table {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.2);
          color: #f87171;
          padding: 8px 16px;
          border-radius: 30px;
          font-size: 0.8rem;
          font-weight: 600;
          transition: all 0.2s ease;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .btn-reset-table:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: scale(1.05);
        }
        .btn-reset-table:disabled {
          opacity: 0.3;
          pointer-events: none;
        }

        /* Toast */
        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 15, 31, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
          animation: slideIn 0.4s ease;
        }
        .toast-item.success { border-left: 4px solid #10b981; }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        /* Modal glass danger */
        .modal-glass .modal-content {
          background: rgba(10, 15, 31, 0.96);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(239, 68, 68, 0.12);
          border-radius: var(--radius);
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(239, 68, 68, 0.08);
          padding: 1.5rem 2rem;
          background: linear-gradient(135deg, rgba(239,68,68,0.08), transparent);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        .modal-glass .modal-body { padding: 2rem; }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(239, 68, 68, 0.08);
          padding: 1.2rem 2rem;
        }

        .btn-reset-cancel {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-secondary);
          padding: 10px 24px;
          border-radius: 30px;
          transition: 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .btn-reset-cancel:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }
        .btn-reset-submit {
          background: #ef4444;
          border: none;
          color: #fff;
          padding: 10px 24px;
          border-radius: 30px;
          font-weight: 600;
          transition: 0.2s;
          box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .btn-reset-submit:hover {
          background: #dc2626;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
        }
        .btn-reset-submit:disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        /* Utilitaires */
        .text-primary { color: var(--accent-blue) !important; }
        .text-secondary { color: var(--text-secondary) !important; }
        .text-danger { color: #ef4444 !important; }
        .mb-1 { margin-bottom: 0.25rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 1rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mt-3 { margin-top: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .justify-content-between { justify-content: space-between; }
        .align-items-center { align-items: center; }
        .opacity-50 { opacity: 0.5; }
        .small { font-size: 0.85rem; }
        .fw-bold { font-weight: 600; }

        @media (max-width: 768px) {
          .reset-container { padding: 1rem; }
          .table-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <ParticleCanvas />

      <div className="reset-container">
        {renderContent()}
      </div>

      {/* Modal de confirmation */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" className="modal-glass">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
            Vider la table
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTable && (
            <div className="text-center">
              <p>
                Vous allez <strong className="text-danger">supprimer définitivement</strong> toutes les données
                de la table <strong>« {selectedTable.name} »</strong>.
              </p>
              <p className="text-secondary small">
                {selectedTable.rows} enregistrement(s) seront supprimés.
              </p>
              <Form.Group className="mt-3">
                <Form.Label className="text-secondary">
                  <FontAwesomeIcon icon={faLock} className="me-2" /> Mot de passe administrateur
                </Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'var(--text-primary)',
                    borderRadius: '12px',
                    padding: '10px 16px'
                  }}
                />
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn-reset-cancel" onClick={() => setShowModal(false)} disabled={resetting}>
            <FontAwesomeIcon icon={faTimes} className="me-2" /> Annuler
          </button>
          <button className="btn-reset-submit" onClick={handleReset} disabled={resetting || !password}>
            {resetting ? (
              <Spinner as="span" animation="border" size="sm" className="me-2" />
            ) : (
              <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
            )}
            Confirmer
          </button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ResetAllData;